import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'
import { z } from 'zod'

/**
 * Wire shape of an FFS indication's thickness stats, stored under
 * `customInfo.ffsStats`. Values are explicit `{ num, unit }` measurements in
 * the CDF's unit. Written by the backend LTA-generation service; the frontend
 * only reads it. Unknown units are rejected rather than fabricated.
 */
const MeasurementJsonSchema = z.object({
  num: z.number(),
  unit: z
    .string()
    .refine((u) => u !== '' && GeckoMeasurementUnit.GetByID(u) !== undefined, {
      message: 'unrecognized measurement unit',
    }),
})

const FfsStatsSchema = z.object({
  minimum: MeasurementJsonSchema,
  mean: MeasurementJsonSchema,
  maximum: MeasurementJsonSchema,
  standardDeviation: MeasurementJsonSchema.optional(),
  tThreshold: MeasurementJsonSchema.optional(),
})

/**
 * Parsed FFS thickness stats as unit-aware `GeckoMeasurement`s, so consumers can
 * convert as needed (e.g. to mm for the corrosion classifier).
 */
export interface FfsStats {
  minimum: GeckoMeasurement
  mean: GeckoMeasurement
  maximum: GeckoMeasurement
  standardDeviation?: GeckoMeasurement
  tThreshold?: GeckoMeasurement
}

/**
 * Parse the `customInfo.ffsStats` slice, fail-closed: returns `null` when the
 * slice is absent or malformed (matching `parseRegionClassification`). Never
 * throws. Call as `parseFfsStats(indication.customInfo?.ffsStats)`.
 */
export function parseFfsStats(raw: unknown): FfsStats | null {
  const parsed = FfsStatsSchema.safeParse(raw)
  if (!parsed.success) return null
  return {
    minimum: GeckoMeasurement.CreateFromJSON(parsed.data.minimum),
    mean: GeckoMeasurement.CreateFromJSON(parsed.data.mean),
    maximum: GeckoMeasurement.CreateFromJSON(parsed.data.maximum),
    ...(parsed.data.tThreshold && {
      tThreshold: GeckoMeasurement.CreateFromJSON(parsed.data.tThreshold),
    }),
    ...(parsed.data.standardDeviation && {
      standardDeviation: GeckoMeasurement.CreateFromJSON(
        parsed.data.standardDeviation
      ),
    }),
  }
}
